/**
 * Text helpers for the workflow run history. Separate module so the component
 * file exports only components (react-refresh) and so these stay unit-testable
 * without rendering anything.
 */

import type { WorkflowRunStep } from '../../agents/workflowsApi.ts';

/** "Scheduled" / "Manual" — a `job:<id>` trigger means a schedule fired it. */
export function triggerLabel(trigger: string): string {
  return trigger.startsWith('job:') ? 'Scheduled' : 'Manual';
}

/** When the run started, in the viewer's locale. The page previously showed
 *  no timestamp at all — only an opaque runId. */
export function runWhenLabel(initiatedAt: string | undefined): string {
  const at = initiatedAt !== undefined ? Date.parse(initiatedAt) : NaN;
  if (!Number.isFinite(at)) {
    return 'Unknown time';
  }
  return new Date(at).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** Failed steps that carry a reason, for the separate alert block. */
export function stepErrors(
  steps: readonly WorkflowRunStep[],
): { index: number; error: string }[] {
  return steps
    .filter(
      (s): s is WorkflowRunStep & { error: string } =>
        s.status === 'failed' && s.error !== undefined && s.error.length > 0,
    )
    .map((s) => ({ index: s.index, error: s.error }));
}

/** One run's step marks — checkmarks only, never failure prose. */
export function stepMarks(steps: readonly WorkflowRunStep[]): string {
  return steps
    .map((s) => {
      const mark =
        s.status === 'succeeded'
          ? '\u2713'
          : s.status === 'failed'
            ? '\u2717'
            : s.status === 'limit-stopped'
              ? '\u23f8 daily limit'
              : '\u2013';
      return `${String(s.index)} ${mark}`;
    })
    .join(' \u00b7 ');
}
